import { BASE_URL, REACT_APP_OLE_API_KEY } from "config";
import { ErrorResponse } from "./types";
import * as Sentry from '@sentry/react';

const HASH_INFO_TAG = 'linkService[getHashInfo]';

const reportHashInfoFailure = (token: string, attempt: number, error: unknown, status?: number) => {
    Sentry.logger.error(`${HASH_INFO_TAG} error`, { session: token, attempt, status, error: String(error) });
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
        tags: { service: HASH_INFO_TAG },
        extra: { session: token, attempt, status },
    });
};

const fetchHashInfo = async (token: string): Promise<any> => {
    const response = await fetch(`${BASE_URL}/api-payments/v1/payments/pending/${token}`,
    {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'X-Api-Key': REACT_APP_OLE_API_KEY,
            'X-User': 'web',
        },
    });
    // The body can only be consumed once: read it a single time and reuse it.
    const body = await response.text();
    if (!response.ok) {
        return { status: response.status, message: body, error: true } as ErrorResponse;
    }
    return JSON.parse(body);
};

// Fetches the payment link session. A network failure, an unparseable body
// or a 5xx is reported to Sentry and retried once before giving up; a 4xx
// (expired or unknown link) is returned as-is without retrying.
const getHashInfo = async (token: string, region: string, retries: number = 1): Promise<any> => {
    for (let attempt = 0; ; attempt++) {
        try {
            const data = await fetchHashInfo(token);
            if (data?.error === true) {
                reportHashInfoFailure(token, attempt, new Error(`${HASH_INFO_TAG} HTTP ${data.status}: ${data.message}`), data.status);
                if (data.status >= 500 && attempt < retries) continue;
                return data;
            }
            Sentry.logger.info(HASH_INFO_TAG, { session: token, body: data });
            return data;
        } catch (error) {
            reportHashInfoFailure(token, attempt, error);
            if (attempt < retries) continue;
            throw new Error("Error inesperado al obtener la sesión de pago.");
        }
    }
}

const isPaymentPending = async (idPayment: number | string): Promise<boolean> => {
    try {
        const response = await fetch(`${BASE_URL}/api-payments/v1/payments/is-pending/${idPayment}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': REACT_APP_OLE_API_KEY,
                'X-User': 'web',
            },
        });
        if (!response.ok) {
            Sentry.logger.error('linkService[isPaymentPending] error', { idPayment, status: response.status, message: await response.text() });
            return true; // fail-open: un error de red no bloquea un pago legítimo
        }
        const data = await response.json();
        Sentry.logger.info('linkService[isPaymentPending]', { idPayment, body: data });
        return data?.is_pending === true;
    } catch (error) {
        Sentry.logger.error('linkService[isPaymentPending] exception', { idPayment, error });
        return true; // fail-open
    }
}

export { getHashInfo, isPaymentPending };